// src/pages/admin/AdminInternationalPage.jsx
// Phase 6 — M6: Admin international management command centre

import { useEffect, useState, useCallback } from 'react'

const PINK  = '#FF3D7F'
const ORG   = '#FF8500'
const DARK  = '#1A1A2E'
const GREY  = '#9CA3AF'
const GR_LT = '#F3F4F6'

const api = (path, opts = {}) =>
  fetch(`/api/v1${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...opts,
  }).then(r => r.json())

const TABS = [
  ['overview',  'Overview'],
  ['countries', 'Countries'],
  ['tax',       'Tax Rules'],
  ['certs',     'Export Certs'],
  ['customs',   'Customs'],
]

const CERT_LABELS = {
  iec: 'IEC', rcmc: 'RCMC', ad_code: 'AD Code',
  fssai: 'FSSAI', brc: 'BRC', oeko_tex: 'OEKO-TEX', gots: 'GOTS', reach: 'REACH',
}

const CUSTOMS_STATUS_STYLES = {
  draft:     { bg: '#F3F4F6', color: '#6B7280' },
  submitted: { bg: '#DBEAFE', color: '#1E40AF' },
  cleared:   { bg: '#DCFCE7', color: '#166534' },
  held:      { bg: '#FEF3C7', color: '#92400E' },
  rejected:  { bg: '#FEE2E2', color: '#991B1B' },
}

function KpiCard({ label, value, sub, color = PINK }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: '1.65rem', fontWeight: 800, color }}>{value}</div>
      <div style={{ fontWeight: 600, color: DARK, fontSize: '0.88rem', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: GREY, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function Toggle({ value, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
      <div
        onClick={() => onChange(!value)}
        style={{
          width: 40, height: 22, borderRadius: 11, position: 'relative',
          background: value ? PINK : '#D1D5DB', transition: 'background 0.2s',
        }}
      >
        <div style={{
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          position: 'absolute', top: 2, left: value ? 20 : 2, transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      {label && <span style={{ fontSize: '0.83rem', color: GREY }}>{label}</span>}
    </label>
  )
}

function StatusPill({ status }) {
  const s = CUSTOMS_STATUS_STYLES[status] ?? { bg: GR_LT, color: GREY }
  return (
    <span style={{ background: s.bg, color: s.color, padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>
      {status?.replace(/_/g, ' ')}
    </span>
  )
}

export default function AdminInternationalPage() {
  const [tab, setTab]               = useState('overview')
  const [overview, setOverview]     = useState(null)
  const [countries, setCountries]   = useState([])
  const [taxRules, setTaxRules]     = useState([])
  const [certs, setCerts]           = useState([])
  const [customs, setCustoms]       = useState([])
  const [loading, setLoading]       = useState(true)
  const [saving, setSaving]         = useState(false)

  // Tax rule form
  const [taxModal, setTaxModal]     = useState(false)
  const [taxForm, setTaxForm]       = useState({ country_code: '', tax_type: 'vat', rate_pct: '', region: '', hs_code_prefix: '', threshold_amount: '', threshold_currency: 'USD', notes: '', effective_from: '' })

  // Filters
  const [certFilter, setCertFilter]     = useState('')
  const [customsFilter, setCustomsFilter] = useState('')
  const [countrySearch, setCountrySearch] = useState('')

  const loadAll = useCallback(() => {
    setLoading(true)
    Promise.all([
      api('/admin/international/overview'),
      api('/admin/international/countries'),
      api('/admin/international/tax-rules'),
      api('/admin/international/certifications'),
      api('/admin/international/customs'),
    ]).then(([oRes, cRes, tRes, certRes, custRes]) => {
      setOverview(oRes.data ?? oRes)
      setCountries(cRes.data ?? [])
      setTaxRules((tRes.data?.data ?? tRes.data ?? []))
      setCerts((certRes.data?.data ?? certRes.data ?? []))
      setCustoms((custRes.data?.data ?? custRes.data ?? []))
      setLoading(false)
    })
  }, [])

  useEffect(() => { loadAll() }, [loadAll])

  const reloadTab = async () => {
    if (tab === 'tax')      { const r = await api(`/admin/international/tax-rules${certFilter ? `?country=${certFilter}` : ''}`); setTaxRules(r.data?.data ?? r.data ?? []) }
    if (tab === 'certs')    { const r = await api('/admin/international/certifications'); setCerts(r.data?.data ?? r.data ?? []) }
    if (tab === 'customs')  { const r = await api(`/admin/international/customs${customsFilter ? `?status=${customsFilter}` : ''}`); setCustoms(r.data?.data ?? r.data ?? []) }
    if (tab === 'countries'){ const r = await api('/admin/international/countries'); setCountries(r.data ?? []) }
  }

  const toggleCountry = async (country, field, value) => {
    const payload = { [field]: value }
    await api(`/admin/international/countries/${country.id}`, { method: 'PUT', body: JSON.stringify(payload) })
    setCountries(prev => prev.map(c => c.id === country.id ? { ...c, [field]: value } : c))
  }

  const upsertTaxRule = async () => {
    setSaving(true)
    await api('/admin/international/tax-rules', { method: 'POST', body: JSON.stringify(taxForm) })
    setSaving(false); setTaxModal(false)
    const r = await api('/admin/international/tax-rules'); setTaxRules(r.data?.data ?? r.data ?? [])
  }

  const verifyCert = async (id) => {
    await api(`/admin/international/certifications/${id}/verify`, { method: 'POST' })
    reloadTab()
  }

  const clearCustoms = async (id) => {
    const ref = window.prompt('Enter customs tracking reference (optional):') ?? ''
    await api(`/admin/international/customs/${id}/clear`, { method: 'POST', body: JSON.stringify({ tracking_reference: ref }) })
    reloadTab()
  }

  const holdCustoms = async (id) => {
    await api(`/admin/international/customs/${id}/hold`, { method: 'POST' })
    reloadTab()
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: GREY }}>Loading international data…</div>

  const exp = overview?.exports ?? {}
  const cust = overview?.customs ?? {}

  const filteredCountries = countrySearch
    ? countries.filter(c => c.country_name.toLowerCase().includes(countrySearch.toLowerCase()) || c.country_code.toLowerCase().includes(countrySearch.toLowerCase()))
    : countries

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 0 56px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.85rem', color: DARK, margin: 0 }}>International Management</h1>
        <p style={{ color: GREY, margin: '4px 0 0', fontSize: '0.88rem' }}>Countries, tax rules, export certifications, and customs oversight</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, background: GR_LT, borderRadius: 12, padding: 4, width: 'fit-content', marginBottom: 24 }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem', background: tab === k ? '#fff' : 'transparent', color: tab === k ? PINK : '#6B7280', boxShadow: tab === k ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>
            {l}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div>
          <p style={{ fontWeight: 700, color: DARK, marginBottom: 10, fontSize: '0.9rem' }}>Export Certifications</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 28 }}>
            <KpiCard label="Total Certs"        value={exp.total_certs           ?? 0} color={DARK} />
            <KpiCard label="Verified"            value={exp.verified              ?? 0} color="#10B981" />
            <KpiCard label="Expiring ≤60 days"  value={exp.expiring_soon         ?? 0} color={exp.expiring_soon > 0 ? ORG : '#10B981'} />
            <KpiCard label="Expired"             value={exp.expired_inactive      ?? 0} color="#EF4444" />
            <KpiCard label="Export-Ready Suppliers" value={exp.export_ready_suppliers ?? 0} color={PINK} />
          </div>
          <p style={{ fontWeight: 700, color: DARK, marginBottom: 10, fontSize: '0.9rem' }}>Customs Declarations</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 28 }}>
            <KpiCard label="Draft"          value={cust.draft       ?? 0} color={GREY}     />
            <KpiCard label="Submitted"      value={cust.submitted   ?? 0} color="#3B82F6"  />
            <KpiCard label="Cleared"        value={cust.cleared     ?? 0} color="#10B981"  />
            <KpiCard label="Held"           value={cust.held        ?? 0} color={ORG}      />
            <KpiCard label="Total Value"    value={`₹${parseFloat(cust.total_value ?? 0).toLocaleString('en-IN')}`} color={DARK} />
          </div>
          <p style={{ fontWeight: 700, color: DARK, marginBottom: 10, fontSize: '0.9rem' }}>Countries</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
            <KpiCard label="Enabled Countries" value={overview.countries ?? 0} color={PINK} />
          </div>
        </div>
      )}

      {/* ── COUNTRIES ── */}
      {tab === 'countries' && (
        <div>
          <input
            placeholder="Search country…"
            value={countrySearch}
            onChange={e => setCountrySearch(e.target.value)}
            style={{ padding: '9px 14px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', width: 280, marginBottom: 16, boxSizing: 'border-box' }}
          />
          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: GR_LT }}>
                  {['Country', 'Code', 'Currency', 'VAT %', 'Tax Regime', 'Enabled', 'Export Allowed', 'Customs Required'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCountries.map((c, i) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: DARK, fontSize: '0.87rem' }}>{c.country_name}</td>
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontWeight: 700, color: PINK, fontSize: '0.85rem' }}>{c.country_code}</td>
                    <td style={{ padding: '12px 14px', color: GREY, fontSize: '0.84rem' }}>{c.currency_code} {c.currency_symbol}</td>
                    <td style={{ padding: '12px 14px', color: DARK, fontWeight: 600, fontSize: '0.84rem' }}>{c.default_vat_rate > 0 ? `${c.default_vat_rate}%` : '—'}</td>
                    <td style={{ padding: '12px 14px', color: GREY, fontSize: '0.8rem' }}>{c.tax_regime ?? '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <Toggle value={!!c.is_enabled} onChange={v => toggleCountry(c, 'is_enabled', v)} />
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <Toggle value={!!c.is_export_allowed} onChange={v => toggleCountry(c, 'is_export_allowed', v)} />
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <Toggle value={!!c.requires_customs_declaration} onChange={v => toggleCountry(c, 'requires_customs_declaration', v)} />
                    </td>
                  </tr>
                ))}
                {filteredCountries.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: GREY }}>No countries found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TAX RULES ── */}
      {tab === 'tax' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <button onClick={() => setTaxModal(true)} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}>
              + Add / Update Rule
            </button>
          </div>
          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: GR_LT }}>
                  {['Country', 'Tax Type', 'Rate %', 'HS Prefix', 'Region', 'Threshold', 'Effective From', 'Notes'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {taxRules.length === 0 && <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: GREY }}>No tax rules yet. Run <code>fabrinza:sync-country-tax-rates</code> to seed defaults.</td></tr>}
                {taxRules.map((r, i) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                    <td style={{ padding: '11px 14px', fontWeight: 800, color: PINK, fontFamily: 'monospace', fontSize: '0.87rem' }}>{r.country_code}</td>
                    <td style={{ padding: '11px 14px', fontSize: '0.84rem', color: DARK }}>{r.tax_type?.replace(/_/g, ' ')}</td>
                    <td style={{ padding: '11px 14px', fontWeight: 700, color: DARK }}>{r.rate_pct}%</td>
                    <td style={{ padding: '11px 14px', color: GREY, fontFamily: 'monospace', fontSize: '0.82rem' }}>{r.hs_code_prefix ?? 'All'}</td>
                    <td style={{ padding: '11px 14px', color: GREY, fontSize: '0.82rem' }}>{r.region ?? 'All'}</td>
                    <td style={{ padding: '11px 14px', color: GREY, fontSize: '0.82rem' }}>{r.threshold_amount > 0 ? `${r.threshold_currency} ${parseFloat(r.threshold_amount).toLocaleString()}` : 'None'}</td>
                    <td style={{ padding: '11px 14px', color: GREY, fontSize: '0.82rem' }}>{r.effective_from ?? '—'}</td>
                    <td style={{ padding: '11px 14px', color: GREY, fontSize: '0.8rem', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tax Rule Modal */}
          {taxModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 520, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto' }}>
                <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>Add / Update Tax Rule</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[['country_code','Country Code (2-letter)','text',1],['tax_type','Tax Type','select',1],['rate_pct','Rate (%)','number',1],['region','Region / State (optional)','text',1],['hs_code_prefix','HS Code Prefix (optional)','text',1],['threshold_amount','De Minimis Threshold','number',1],['threshold_currency','Threshold Currency','text',1],['notes','Notes','text',2],['effective_from','Effective From','date',2]].map(([k, l, t, cols]) => (
                    <div key={k} style={{ gridColumn: `span ${cols}` }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>{l}</label>
                      {t === 'select' ? (
                        <select value={taxForm[k]} onChange={e => setTaxForm(f => ({ ...f, [k]: e.target.value }))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem' }}>
                          {['vat', 'gst', 'sales_tax', 'customs_duty', 'consumption_tax', 'none'].map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
                        </select>
                      ) : (
                        <input type={t} value={taxForm[k]} onChange={e => setTaxForm(f => ({ ...f, [k]: e.target.value }))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                  <button onClick={() => setTaxModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={upsertTaxRule} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save Rule'}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── EXPORT CERTIFICATIONS ── */}
      {tab === 'certs' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {['', 'iec', 'rcmc', 'ad_code', 'oeko_tex', 'gots', 'reach'].map(ct => (
              <button key={ct} onClick={() => setCertFilter(ct)} style={{ padding: '6px 14px', borderRadius: 10, border: `1.5px solid ${certFilter === ct ? PINK : '#E5E7EB'}`, background: certFilter === ct ? PINK : '#fff', color: certFilter === ct ? '#fff' : '#6B7280', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                {ct === '' ? 'All' : CERT_LABELS[ct] ?? ct}
              </button>
            ))}
          </div>
          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: GR_LT }}>
                  {['Supplier', 'Type', 'Cert Number', 'Issuing Authority', 'Expiry', 'Verified', 'Action'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {certs.filter(c => !certFilter || c.cert_type === certFilter).length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: GREY }}>No certifications found.</td></tr>
                )}
                {certs.filter(c => !certFilter || c.cert_type === certFilter).map((cert, i) => {
                  const days    = cert.expiry_date ? Math.floor((new Date(cert.expiry_date) - new Date()) / 86400000) : null
                  const expired = days !== null && days < 0
                  const expiring = days !== null && days >= 0 && days <= 60
                  return (
                    <tr key={cert.id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      <td style={{ padding: '12px 14px', fontWeight: 600, color: DARK, fontSize: '0.87rem' }}>{cert.supplier?.business_name ?? cert.supplier?.name ?? `#${cert.supplier_id}`}</td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ background: '#EDE9FE', color: '#5B21B6', padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>{CERT_LABELS[cert.cert_type] ?? cert.cert_type}</span>
                      </td>
                      <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: '0.82rem', color: '#374151' }}>{cert.cert_number}</td>
                      <td style={{ padding: '12px 14px', fontSize: '0.82rem', color: GREY, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cert.issuing_authority ?? '—'}</td>
                      <td style={{ padding: '12px 14px', fontSize: '0.82rem', color: expired ? '#EF4444' : expiring ? ORG : GREY, fontWeight: expired || expiring ? 700 : 400 }}>
                        {cert.expiry_date ? (expired ? '⚠️ Expired' : expiring ? `⚠️ ${days}d left` : new Date(cert.expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })) : '—'}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{ color: cert.is_verified ? '#10B981' : ORG, fontWeight: 700, fontSize: '0.82rem' }}>
                          {cert.is_verified ? '✓ Verified' : '○ Pending'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        {!cert.is_verified && (
                          <button onClick={() => verifyCert(cert.id)} style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: '#10B981', color: '#fff', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                            Verify
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── CUSTOMS DECLARATIONS ── */}
      {tab === 'customs' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {['', 'draft', 'submitted', 'cleared', 'held', 'rejected'].map(s => (
              <button key={s} onClick={() => setCustomsFilter(s)} style={{ padding: '6px 14px', borderRadius: 10, border: `1.5px solid ${customsFilter === s ? PINK : '#E5E7EB'}`, background: customsFilter === s ? PINK : '#fff', color: customsFilter === s ? '#fff' : '#6B7280', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: GR_LT }}>
                  {['Declaration No.', 'Supplier', 'Order', 'Destination', 'Value (INR)', 'Duty Est.', 'Incoterm', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {customs.filter(d => !customsFilter || d.status === customsFilter).length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 32, textAlign: 'center', color: GREY }}>No declarations found.</td></tr>
                )}
                {customs.filter(d => !customsFilter || d.status === customsFilter).map((d, i) => (
                  <tr key={d.id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: DARK, fontFamily: 'monospace', fontSize: '0.83rem' }}>{d.declaration_number}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.84rem', color: DARK }}>{d.supplier?.business_name ?? d.supplier?.name ?? `#${d.supplier_id}`}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.84rem', color: GREY }}>#{d.order_id}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 800, color: PINK, fontFamily: 'monospace', fontSize: '0.87rem' }}>{d.destination_country}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: DARK, fontSize: '0.84rem' }}>₹{parseFloat(d.declared_value_inr ?? 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', color: ORG, fontWeight: 600, fontSize: '0.84rem' }}>₹{parseFloat(d.customs_duty_estimate ?? 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px', color: GREY, fontSize: '0.82rem' }}>{d.incoterm}</td>
                    <td style={{ padding: '12px 14px' }}><StatusPill status={d.status} /></td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {d.status === 'submitted' && (
                          <button onClick={() => clearCustoms(d.id)} style={{ padding: '4px 10px', borderRadius: 7, border: 'none', background: '#10B981', color: '#fff', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>Clear</button>
                        )}
                        {['submitted', 'draft'].includes(d.status) && (
                          <button onClick={() => holdCustoms(d.id)} style={{ padding: '4px 10px', borderRadius: 7, border: `1.5px solid ${ORG}`, background: '#fff', color: ORG, fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>Hold</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
